import { storeToRefs } from 'pinia'
import { v4 as uuid } from 'uuid'
import type { Ref } from 'vue'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { BillGenerator } from '@last/core'
import { useDialog } from '@last/core-ui/paprika'

import type { CalculatorPayload } from '@/components/Calculator/types'
import { useNotifications } from '@/composables/useNotifications'
import { useTabs } from '@/composables/useTabs'
import { t } from '@/i18n'
//import { AdyenIPPPlugin } from '@/integrations/adyen/AdyenIPPPlugin'
import CashMachine from '@/integrations/cashmachine/cashmachine'
import Dataphone from '@/integrations/dataphone/dataphone'
import { iZettlePlugin } from '@/integrations/iZettlePlugin'
import Printer from '@/integrations/printer/printer'
import TicketPrinter from '@/integrations/printer/ticketPrinter'
import { SumUpPlugin } from '@/integrations/SumUpPlugin'
import { logger } from '@/monitoring'
import { useBillingStore } from '@/store/billing'
import { useConfigStore } from '@/store/config'
import { usePromotionsStore } from '@/store/promotions'
import { useTabsStore } from '@/store/tabs'
import type { Bill, BillWithPayments, PaymentMethod } from '@/types'

import { useMoney } from './useMoney'

export const useCheckout = (
  tabId: string,
  checkoutType: 'normalCheckout' | 'fastCheckout' = 'normalCheckout',
  calculatorPayload: Ref<CalculatorPayload> = ref({
    pending: 0,
    change: 0,
    toPay: 0,
    tip: 0
  })
) => {
  const billingStore = useBillingStore()
  const { deleteTabPromotion, getTabGlobalPromotion } = usePromotionsStore()
  const { tab, allProducts, allBills, pendingBill, globalDiscount } = useTabs(tabId)
  const { addBill, addPayment, closeTab } = useTabsStore()
  const { getCurrentBill } = storeToRefs(billingStore)
  const { config } = useConfigStore()
  const { currency } = useMoney()
  const dialog = useDialog()
  const { notifyInfo, notifyError } = useNotifications()
  const router = useRouter()
  const route = useRoute()

  const selectedBillId = ref<string>('')
  const selectedProductsIds = ref<{ id: string; uniqueId: string }[]>([])
  const isCharging = ref<boolean>(false)

  const currentDiscount = computed(() => {
    return globalDiscount.value
  })

  const currentBill = computed<Bill>(() => {
    if (checkoutType === 'fastCheckout') {
      const pendingBills = allBills.value.filter(bill => bill.pending && bill.pending > 0)
      if (pendingBills.length > 0) {
        selectedBillId.value = pendingBills[0].id
        return pendingBills[0]
      } else {
        const bill = pendingBill.value
        if (!bill) return
        const extendedBill = BillGenerator.addPaymentInfo(bill, [])
        selectedBillId.value = extendedBill.id

        return extendedBill
      }
    }

    if (checkoutComplete.value) return null
    const current = getCurrentBill.value({
      tabId: tabId,
      currentDiscount: currentDiscount.value,
      selectedProductIds: selectedProductsIds.value
    })
    selectedBillId.value = current.id

    return current
  })

  const selectedBill = computed<BillWithPayments>(() => {
    return bills.value.find(bill => bill.id === selectedBillId.value)!
  })

  const bills = computed<BillWithPayments[]>(() => {
    const existingBills = allBills.value
    if (checkoutComplete.value) {
      return existingBills
    } else {
      const currentBillWithPaymentInfo = BillGenerator.addPaymentInfo(currentBill.value, [])

      return [currentBillWithPaymentInfo, ...existingBills]
    }
  })

  const pendingBills = computed<BillWithPayments[]>(() => {
    return bills.value.filter(bill => bill.pending && bill.pending > 0) || []
  })

  const paymentMethods = computed<PaymentMethod[]>(() => {
    const methods = [...config.paymentMethodsConfig]
      .sort((a, b) => a.position - b.position)
      .map(method => {
        const type = method.name.toLowerCase()
        return {
          ...method,
          name: t(`bill.${type}`, method.name),
          type
        }
      })
    /* if (AdyenIPPPlugin.isAvailable()) {
      methods = [
        {
          name: 'LastPay',
          type: 'lastpay',
          openDrawer: false,
          availableForDelivery: true,
          availableForShift: false,
          position: 0
        },
        ...methods
      ]
    } */
    return methods
  })

  const cashPaymentMethod = computed(() => {
    return paymentMethods.value.find(method => method.type === 'cash')
  })

  const paymentMethod = ref<PaymentMethod>(cashPaymentMethod.value ?? paymentMethods.value[0])

  const toCharge = computed(() => {
    if (paymentMethod.value.type === 'cash' && selectedBill.value) {
      return Math.min(selectedBill.value.pending, calculatorPayload.value.toPay)
    } else {
      return calculatorPayload.value.toPay
    }
  })

  const canCharge = computed(() => {
    if (!selectedBill.value) return false
    if (checkoutType === 'fastCheckout') {
      return toCharge.value >= currentBill.value.pending! && !isCharging.value
    }

    return toCharge.value > 0 || selectedBill.value.total === 0
  })

  function selectPaymentMethod(method: string): void {
    const payment = paymentMethods.value.find(m => m.type === method)
    if (payment) {
      paymentMethod.value = payment
    }
  }

  async function charge(avoidConfirm?: boolean): Promise<void> {
    if (!canCharge.value) return
    isCharging.value = true

    const paymentId = uuid()

    if (CashMachine.methods.includes(paymentMethod.value.type)) {
      await chargeWithMethod()
    } else if (paymentMethod.value.type === 'cash') {
      executePayment(paymentId)
      if (calculatorPayload.value.change > 0 && !avoidConfirm) {
        showChangeConfirmationDialog()
      }
    } else {
      if (calculatorPayload.value.tip > 0 && !avoidConfirm) {
        showConfirmTipDialog()
      } else {
        await chargeWithMethod()
      }
    }

    if (paymentMethod.value.openDrawer) {
      Printer.openCashDrawer()
    }

    isCharging.value = false
  }

  async function chargeWithMethod(): Promise<void> {
    try {
      const paymentId = uuid()
      let metadata = null

      const isTerminal = config.terminals.some(
        terminal => terminal.paymentMethod === paymentMethod.value.name
      )
      /* if (paymentMethod.value.type === 'lastpay') {
        metadata = await AdyenIPPPlugin.charge({
          amount: toCharge.value,
          paymentId: paymentId,
          tabId
        })
      } else  */ if (paymentMethod.value.type === 'izettle') {
        await iZettlePlugin.charge({ amount: toCharge.value })
      } else if (paymentMethod.value.type === 'sumup') {
        await SumUpPlugin.charge({ amount: toCharge.value })
      } else if (CashMachine.methods.includes(paymentMethod.value.type)) {
        const charged = await CashMachine.charge(toCharge.value)
        if (charged === 0) throw new Error('CashMachine charging failed')
      } else if (paymentMethod.value.type === 'dataphone') {
        metadata = await Dataphone.charge({
          amount: toCharge.value,
          tip: calculatorPayload.value.tip,
          paymentId,
          billId: selectedBill.value.id,
          tabId: tabId
        })
        // Only used for adyen dataphone
        if (metadata.tip) updateTip(metadata.tip)
      } else if (isTerminal) {
        const chargeResponse = await Dataphone.charge({
          amount: toCharge.value,
          tip: calculatorPayload.value.tip,
          paymentId,
          billId: selectedBill.value.id,
          tabId: tabId
        })
        if (chargeResponse.tip) updateTip(chargeResponse.tip)
      }
      executePayment(paymentId, metadata)
    } catch (error) {
      logger.warn('Payment error:', error as Error)
      const message = (error as Error).message
      notifyError({
        title: message
          ? `${t('notifications.payment-failed')}: ${message}`
          : t('notifications.payment-failed')
      })
    }
  }

  function executePayment(paymentId: string, metadata?: any): void {
    const bill = selectedBill.value
    const billId = bill.id
    const amount = toCharge.value
    addBill({ tabId, bill })
    addPayment({
      type: paymentMethod.value.type,
      amount,
      change: calculatorPayload.value.change,
      tip: calculatorPayload.value.tip,
      billId,
      paymentId,
      metadata,
      tabId: tabId,
      tillId: undefined
    })
    const printBill = bills.value.find(bill => bill.id === billId)!
    const pending = printBill.pending ?? selectedBill.value.pending
    if (
      pending <= 0 &&
      (((tab.value.tables?.length || 0) === 0 && config.organizationConfig.takeawayAutoprint) ||
        config.organizationConfig.restaurantAutoprint)
    ) {
      TicketPrinter.printBill(printBill)
    }

    onPaymentSuccess()
  }

  function onPaymentSuccess(): void {
    if (checkoutType === 'fastCheckout') {
      closeTab({ tabId, closedWithPin: false })

      if (route && route.name !== 'pos' && route.name !== 'fastMode') {
        router.push({ name: 'pos' })
      }

      notifyInfo({
        title: t('notifications.tab-closed'),
        onAction: () => {
          router.push({
            name: 'tabs',
            query: { preSelectedTabId: tabId }
          })
        }
      })
      isCharging.value = false

      return
    }

    selectedProductsIds.value = []
    if (selectedBill.value.pending === 0 && currentBill.value) {
      selectedBillId.value = currentBill.value.id
    } else if (selectedBill.value.pending === 0 && pendingBills.value.length > 0) {
      selectedBillId.value = pendingBills.value[0].id
    }
    isCharging.value = false
  }

  const checkoutComplete = computed(() => {
    const tabProducts = allProducts.value
    return (
      tabProducts
        .map(product => product.notBilledQuantity)
        .reduce((total, quantity) => total + quantity, 0) === 0
    )
  })

  const checkoutCompleteWithPayments = computed<boolean>(() => {
    const tabProducts = allProducts.value
    const allProductsBilled =
      tabProducts
        .map(product => product.notBilledQuantity)
        .reduce((total, quantity) => total + quantity, 0) === 0
    const pendingPayments = bills.value
      .map(bill => bill.pending)
      .reduce((sum, pending) => sum + pending, 0)
    return allProductsBilled && pendingPayments === 0
  })

  function showConfirmTipDialog(): void {
    dialog({
      title: t('checkout.confirm-tip', {
        tip: currency(calculatorPayload.value.tip)
      }),
      secondaryLabel: t('ctas.cancel'),
      onConfirm: () => {
        charge(true)
      },
      onSecondary: () => {
        isCharging.value = false
      },
      onCancel: () => {
        isCharging.value = false
      }
    })
  }

  function showChangeConfirmationDialog(): void {
    dialog({
      title: t('checkout.change'),
      content: `${t('checkout.change-info')} ${currency(calculatorPayload.value.change)}`
    })
  }

  async function removeDiscount(): Promise<void> {
    await deleteTabPromotion({
      tabId,
      tabPromotion: getTabGlobalPromotion(tabId)!
    })
  }

  async function updateTip(tip: number): Promise<void> {
    calculatorPayload.value.toPay -= calculatorPayload.value.tip

    calculatorPayload.value.tip = tip
    calculatorPayload.value.toPay += tip
  }

  return {
    bills,
    selectedBill,
    selectedBillId,
    currentBill,
    pendingBills,
    selectedProductsIds,
    checkoutComplete,
    checkoutCompleteWithPayments,
    paymentMethods,
    paymentMethod,
    isCharging,
    canCharge,
    charge,
    selectPaymentMethod,
    removeDiscount
  }
}
